import type { AdminAccount, AdminAlert, AdminCase, AdminOverview, AdminPayoutAccount, AdminPort, AdminReport, AuditEntry, ProblemsPort, ProblemView, StaffRole, AdminUserPage, CsvExport, GrowthView } from './ports.ts';
import type { WorkerClient } from './worker.ts';

const text = (value: unknown) => (typeof value === 'string' ? value : '');

function problem(item: Record<string, unknown> | null): ProblemView | null {
  if (!item) return null;
  return {
    orderId: text(item.orderId || item.id), status: text(item.status) as ProblemView['status'],
    kind: (['cancelled', 'rejected', 'timeout'].includes(text(item.kind)) ? text(item.kind) : 'problem') as ProblemView['kind'],
    message: text(item.message), reportMessage: text(item.reportMessage) || null,
    amountMinor: Math.round(Number(item.amountMinor) || 0), feeKeptMinor: Math.round(Number(item.feeKeptMinor) || 0),
    dueAt: text(item.dueAt) || null, restricted: item.restricted === true,
    sellerEvidence: text(item.refundReference) || text(item.sellerEvidence) || null, proofUrl: text(item.proofUrl) || null, createdAt: text(item.createdAt), shopName: text(item.shopName),
    refundDestination: text(item.refundDestination) || null,
  };
}

export function createProblems(worker: WorkerClient): ProblemsPort {
  const item = async (type: string, body: Record<string, unknown>) => problem((await worker.call<{ item: Record<string, unknown> | null }>(type, body)).item)!;
  return {
    get: async orderId => problem((await worker.call<{ item: Record<string, unknown> | null }>('v2_get_problem', { orderId })).item),
    report: (orderId, message) => item('v2_report_problem', { orderId, message, contactedSeller: true }),
    recordRefund: (orderId, reference, proofUrl) => item('v2_record_refund', { orderId, reference, ...(proofUrl ? { proofUrl } : {}) }),
    confirmRefund: orderId => item('v2_confirm_refund', { orderId }),
    setDestination: (orderId, destination) => item('v2_set_refund_destination', { orderId, destination }),
  };
}

// Staff tools. The Worker checks the staff role on every call.
export function createAdmin(worker: WorkerClient): AdminPort {
  return {
    whoami: () => worker.call<StaffRole>('admin_whoami'),
    overview: () => worker.call<AdminOverview>('admin_overview'),
    cases: async status => (await worker.call<{ items: Record<string, unknown>[] }>('admin_list_cases', status ? { status } : {})).items
      .map((item): AdminCase => ({
        ...problem(item)!, buyerId: text(item.buyerId), sellerId: text(item.sellerId), shopId: text(item.shopId) || null,
        history: Array.isArray(item.history) ? item.history as AdminCase['history'] : [],
      })),
    resolveCase: async (orderId, outcome, note, requestKey) => { await worker.call('admin_resolve_case', { orderId, outcome, note, ...(requestKey ? { requestKey } : {}) }); },
    messageCaseSeller: async (orderId, note, requestKey) => { await worker.call('admin_message_case_seller', { orderId, note, requestKey }); },
    setSellerStatus: async (uid, change, note) => { await worker.call('admin_set_seller_status', { uid, ...change, note }); },
    alerts: async () => (await worker.call<{ items: AdminAlert[] }>('admin_list_alerts')).items,
    closeAlert: async (alertId, note) => { await worker.call('admin_close_alert', { alertId, note }); },
    lookup: async query => (await worker.call<{ account: AdminAccount | null }>('admin_lookup_account', { query })).account,
    setSuspended: async (uid, suspended, note) => { await worker.call('admin_set_suspended', { uid, suspended, note }); },
    setOfficial: async (uid, official) => { await worker.call('admin_set_official', { uid, official }); },
    payoutAccounts: async status => (await worker.call<{ items: AdminPayoutAccount[] }>('admin_list_payout_accounts', { status })).items,
    audit: async () => (await worker.call<{ items: AuditEntry[] }>('admin_audit_log')).items,
    reports: async () => (await worker.call<{ items: AdminReport[] }>('list_support_inbox', { queue: 'report', limit: 200 })).items,
    setReportStatus: async (itemId, status, note) => { await worker.call('update_support_inbox', { queue: 'report', itemId, status, ...(note ? { internalNote: note } : {}) }); },
    listUsers: (input = {}) => worker.call<AdminUserPage>('admin_list_users', input),
    ban: async (uid, note) => { await worker.call('admin_ban_account', { uid, note }); },
    unban: async (uid, note, requestKey) => { await worker.call('admin_unban_account', { uid, note, requestKey }); },
    reportPage: (input = {}) => worker.call('admin_list_reports', input),
    actOnReport: input => worker.call('admin_act_on_report', input),
    exportAudit: (input = {}) => worker.call<CsvExport>('admin_export_audit', input),
    growth: days => worker.call<GrowthView>('admin_growth', { days }),
    exportGrowth: days => worker.call<CsvExport>('admin_growth_export', { days }),
  };
}
